import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useTenant,
  useTenantContacts,
  useTenantCredentials,
  useUpdateTenantCredentials,
  useUpdateTenant,
  useDeployTenant,
  useDeployReadiness,
  useDeploymentPoller,
} from "@/hooks/useTenants";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Icon } from "@/components/ui/icons";
import {
  StatusBadge,
  DeployBadge,
  TrialBadge,
  OriginBadge,
} from "@/components/ui/status-badges";
import {
  Btn,
  Field,
  NativeSelect,
  Modal,
} from "@/components/ui/page-components";
import { Avatar } from "@/components/ui/avatar";
import type { Tenant } from "@/types";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import TenantUsersTab from "./TenantUsersTab";
import TenantConfigTab from "./TenantConfigTab";
import TenantServicesTab from "./TenantServicesTab";
import LoadTestTab from "./LoadTestTab";

const DEPLOY_STEP_META: Record<string, { label: string; icon: string; isManual?: boolean }> = {
  validate: { label: "Validate configuration", icon: "check" },
  create_database: { label: "Provision database", icon: "database" },
  register_database: { label: "Register DB alias", icon: "link" },
  run_migrations: { label: "Run migrations", icon: "refresh" },
  seed_data: { label: "Seed service catalog", icon: "layers" },
  create_admin: { label: "Create admin user", icon: "userPlus" },
  seed_venue_config: { label: "Seed venue config", icon: "settings" },
  feature_flags: { label: "Configure feature flags", icon: "flag" },
  deploy_booking_pages: { label: "Deploy booking pages", icon: "globe" },
};

const DEPLOY_STEP_KEYS = Object.keys(DEPLOY_STEP_META);

const VENUE_LABEL: Record<string, string> = {
  simulator: "Simulator",
  golf_course: "Golf Course",
};

const DETAIL_TABS = [
  { id: "info", label: "Info", icon: "building" },
  { id: "services", label: "Services", icon: "plug" },
  { id: "credentials", label: "Credentials", icon: "key" },
  { id: "contacts", label: "Contacts", icon: "mail" },
  { id: "users", label: "Users", icon: "users" },
  { id: "config", label: "Venue Config", icon: "settings" },
  { id: "load-test", label: "Load Test", icon: "activity" },
];

/* ---------- Deployment Panel ---------- */
function DeploymentPanel({
  status,
  job,
  isDeploying,
}: {
  status?: string;
  job?: import("@/types").DeploymentJob | null;
  isDeploying: boolean;
}) {
  const logRef = useRef<HTMLPreElement>(null);
  const [open, setOpen] = useState(false);
  const failed = status === "failed" && !isDeploying;

  const stepsCompleted = job?.steps_completed ?? [];
  const completedKeys = new Set(stepsCompleted.map((s) => s.step));
  const stepCount = stepsCompleted.length;
  const total = DEPLOY_STEP_KEYS.length;
  const currentStepKey = isDeploying
    ? DEPLOY_STEP_KEYS[Math.min(stepCount, total - 1)]
    : null;

  const logText = job?.log || "";

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logText]);

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <span
          className={cn(
            "flex h-9 w-9 items-center justify-center rounded-lg",
            isDeploying
              ? "bg-blue-100 text-blue-600"
              : failed
                ? "bg-red-100 text-red-600"
                : "bg-green-100 text-green-600",
          )}
        >
          <Icon
            name={isDeploying ? "refresh" : failed ? "alert" : "rocket"}
            size={18}
            className={isDeploying ? "animate-spin" : ""}
          />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[13px] font-bold text-foreground">
            {isDeploying
              ? "Deployment in progress"
              : failed
                ? "Last deployment failed"
                : "Deployment healthy"}
            <DeployBadge status={isDeploying ? "deploying" : status} />
          </div>
          <div className="text-[11px] text-muted-foreground">
            {isDeploying
              ? `Step ${stepCount} of ${total} · ${DEPLOY_STEP_META[currentStepKey!]?.label ?? "Processing…"}`
              : failed
                ? "Deployment failed — expand log for details"
                : `${stepCount} of ${total} steps completed`}
          </div>
        </div>
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1 rounded-md px-2 py-1 text-[12px] font-semibold text-muted-foreground hover:bg-muted"
        >
          <Icon name="list" size={14} /> Log{" "}
          <Icon name={open ? "chevronUp" : "chevronDown"} size={14} />
        </button>
      </div>
      {(isDeploying || stepCount > 0) && (
        <div className="px-4 pb-3">
          <div className="flex items-center gap-1.5">
            {DEPLOY_STEP_KEYS.map((key, i) => {
              const meta = DEPLOY_STEP_META[key];
              const stepData = stepsCompleted.find((s) => s.step === key);
              const isCompleted = completedKeys.has(key);
              const isCurrent = i === stepCount && isDeploying;
              const isManual = stepData?.status === "manual";
              const isSkipped = stepData?.status === "skipped";

              return (
                <div key={key} className="group relative flex-1">
                  <div
                    className={cn(
                      "h-1.5 rounded-full transition-colors duration-300",
                      isManual
                        ? "bg-amber-400"
                        : isSkipped
                          ? "bg-slate-300"
                          : isCompleted
                            ? "bg-primary"
                            : isCurrent
                              ? "bg-blue-400 animate-pulse"
                              : "bg-muted",
                    )}
                  />
                  <span className="pointer-events-none absolute -top-7 left-1/2 z-10 hidden -translate-x-1/2 whitespace-nowrap rounded bg-gray1 px-1.5 py-1 text-[10px] text-white group-hover:block">
                    {meta.label}
                    {isManual && " ⚠ Manual"}
                    {isSkipped && " (skipped)"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {open && (
        <pre
          ref={logRef}
          className="max-h-56 overflow-y-auto border-t border-border bg-[#0e1622] px-4 py-3 font-mono text-[11px] leading-relaxed text-slate-200"
        >
          {logText || "No deployment logs yet."}
          {isDeploying && "\n▌"}
        </pre>
      )}
    </Card>
  );
}

/* ---------- Section Card ---------- */
function SectionCard({
  title,
  action,
  children,
  className,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Card className={cn("overflow-hidden", className)}>
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-[13px] font-bold text-foreground">{title}</h3>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </Card>
  );
}

/* ---------- KV row ---------- */
function KV({
  icon,
  label,
  value,
  mono,
}: {
  icon: string;
  label: string;
  value?: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <Icon name={icon} size={14} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div
          className={cn(
            "truncate text-[12px] text-foreground",
            mono && "font-mono",
          )}
        >
          {value || "—"}
        </div>
      </div>
      {value && (
        <button
          onClick={() => {
            navigator.clipboard.writeText(value);
            toast.success("Copied");
          }}
          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Icon name="copy" size={13} />
        </button>
      )}
    </div>
  );
}

/* ---------- Info Tab ---------- */
function InfoTab({
  tenant,
  onSave,
}: {
  tenant: Tenant;
  onSave: (data: Partial<Tenant>) => void;
}) {
  const [form, setForm] = useState<{
    name: string;
    slug: string;
    domain: string;
    status: Tenant["status"];
    notes: string;
  }>({
    name: tenant.name,
    slug: tenant.slug,
    domain: tenant.domain || "",
    status: tenant.status,
    notes: "",
  });
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <SectionCard title="Identity" className="lg:col-span-2">
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
          <Field label="Venue name">
            <Input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
            />
          </Field>
          <Field label="Slug">
            <Input
              value={form.slug}
              onChange={(e) => set("slug", e.target.value)}
            />
          </Field>
          <Field label="Domain">
            <Input
              value={form.domain}
              onChange={(e) => set("domain", e.target.value)}
              placeholder="venue.com"
            />
          </Field>
          <Field label="Status">
            <NativeSelect
              value={form.status}
              onChange={(v) => set("status", v)}
            >
              <option value="active">Active</option>
              <option value="provisioning">Provisioning</option>
              <option value="suspended">Suspended</option>
              <option value="archived">Archived</option>
            </NativeSelect>
          </Field>
        </div>
        <div className="mt-4 border-t border-border pt-4">
          <Field label="Internal notes">
            <Textarea
              rows={3}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Notes about this tenant…"
            />
          </Field>
        </div>
        <div className="mt-4 flex justify-end">
          <Btn icon="check" onClick={() => onSave(form)}>
            Save changes
          </Btn>
        </div>
      </SectionCard>
      <div className="space-y-4">
        <SectionCard title="Infrastructure">
          <div className="space-y-3">
            <KV icon="globe" label="API URL" value={tenant.api_url} mono />
            <KV
              icon="database"
              label="DB host"
              value={tenant.database_host}
              mono
            />
            <KV
              icon="server"
              label="DB name"
              value={tenant.database_name}
              mono
            />
          </div>
        </SectionCard>
        <SectionCard title="Quick Links">
          <div className="space-y-2">
            <a
              href={`https://admin.teelogix.io/?tenant=${tenant.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-left hover:bg-muted"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Icon name="layout" size={16} />
              </span>
              <span className="text-[13px] font-medium text-foreground">
                Admin Dashboard
              </span>
              <Icon name="arrowRight" size={14} className="ml-auto text-muted-foreground" />
            </a>
            {tenant.domain && (
              <a
                href={`https://${tenant.domain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-left hover:bg-muted"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                  <Icon name="globe" size={16} />
                </span>
                <span className="text-[13px] font-medium text-foreground">
                  Customer Website
                </span>
                <Icon name="arrowRight" size={14} className="ml-auto text-muted-foreground" />
              </a>
            )}
          </div>
        </SectionCard>
        <SectionCard title="Venue type">
          <div className="flex flex-wrap gap-2">
            {(tenant.venue_types || []).map((v: string) => (
              <span
                key={v}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-2.5 py-1 text-[12px] font-semibold text-foreground"
              >
                <Icon name={v === "simulator" ? "monitor" : "flag"} size={12} />
                {VENUE_LABEL[v] || v}
              </span>
            ))}
            <span className="inline-flex items-center rounded-full border border-border bg-card px-2.5 py-1 text-[12px] font-medium text-muted-foreground">
              {tenant.is_private ? "Private" : "Public"}
            </span>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

/* ---------- Contacts Tab ---------- */
function ContactsTab({ tenant }: { tenant: Tenant }) {
  const { data: contacts = [] } = useTenantContacts(tenant.id);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "primary",
  });

  return (
    <SectionCard
      title="Contacts"
      action={
        <Btn size="sm" icon="plus" onClick={() => setOpen(true)}>
          Add contact
        </Btn>
      }
    >
      <div className="divide-y divide-border/60">
        {contacts.map((c) => (
          <div key={c.id} className="flex items-center gap-3 py-2.5">
            <Avatar name={c.name} size={34} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-semibold text-foreground">
                  {c.name}
                </span>
                <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold capitalize text-muted-foreground">
                  {c.role}
                </span>
              </div>
              <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Icon name="mail" size={11} />
                  {c.email}
                </span>
                {c.phone && (
                  <span className="inline-flex items-center gap-1">
                    <Icon name="phone" size={11} />
                    {c.phone}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
        {contacts.length === 0 && (
          <p className="py-8 text-center text-[12px] text-muted-foreground">
            No contacts yet.
          </p>
        )}
      </div>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Add contact"
        description="Add a contact person for this tenant."
        footer={
          <>
            <Btn variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Btn>
            <Btn
              disabled={!form.name || !form.email}
              onClick={() => {
                toast.success("Contact added");
                setOpen(false);
              }}
            >
              Add contact
            </Btn>
          </>
        }
      >
        <div className="space-y-3 py-1">
          <Field label="Name">
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email">
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </Field>
            <Field label="Phone">
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </Field>
          </div>
          <Field label="Role">
            <NativeSelect
              value={form.role}
              onChange={(v) => setForm({ ...form, role: v })}
            >
              <option value="primary">Primary</option>
              <option value="billing">Billing</option>
              <option value="technical">Technical</option>
            </NativeSelect>
          </Field>
        </div>
      </Modal>
    </SectionCard>
  );
}

/* ---------- Credentials Tab ---------- */
const CRED_GROUP_ICONS: Record<string, string> = {
  Clover: "zap",
  Square: "zap",
  Twilio: "phone",
  Notifications: "mail",
};

function CredentialsTab({ tenant }: { tenant: Tenant }) {
  const { data, isLoading } = useTenantCredentials(tenant.id);
  const updateMutation = useUpdateTenantCredentials();
  const [edits, setEdits] = useState<Record<string, string>>({});

  if (isLoading) {
    return (
      <SectionCard title="API Credentials">
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </SectionCard>
    );
  }

  const databaseReady = data?.database_ready ?? false;
  const credentials = data?.credentials || [];

  // Group credentials by provider for readability
  const groups: Record<string, typeof credentials> = {};
  for (const c of credentials) {
    (groups[c.group] ||= []).push(c);
  }

  const dirty = Object.keys(edits).length > 0;

  const save = async () => {
    const payload = Object.entries(edits).map(([key, value]) => ({
      key,
      value,
    }));
    try {
      await updateMutation.mutateAsync({
        tenantId: tenant.id,
        credentials: payload,
      });
      setEdits({});
      toast.success("Credentials updated");
    } catch {
      toast.error("Failed to update credentials");
    }
  };

  return (
    <div className="space-y-4">
      <Card className="flex items-start gap-3 border-amber-200 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
        <span className="mt-0.5 text-amber-600">
          <Icon name="shield" size={18} />
        </span>
        <div className="text-[12.5px] leading-relaxed text-amber-900 dark:text-amber-200">
          <p className="font-semibold">Per-tenant secrets</p>
          <p className="text-amber-800/90 dark:text-amber-200/80">
            These keys are unique to this venue and stored encrypted in its own
            database. Leave a field blank to keep the shared platform default.
            Saved values are never shown again — only a masked preview.
          </p>
        </div>
      </Card>

      {!databaseReady && (
        <Card className="flex items-center gap-3 border-border bg-muted/40 p-4">
          <Icon name="alert" size={18} className="text-orange2" />
          <p className="text-[12.5px] text-muted-foreground">
            This tenant's database isn't deployed yet. Deploy the tenant before
            configuring credentials.
          </p>
        </Card>
      )}

      {Object.entries(groups).map(([group, items]) => (
        <SectionCard
          key={group}
          title={group}
          action={
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-muted text-muted-foreground">
              <Icon name={CRED_GROUP_ICONS[group] || "key"} size={14} />
            </span>
          }
        >
          <div className="space-y-3.5">
            {items.map((c) => {
              const edited = c.key in edits;
              return (
                <div
                  key={c.key}
                  className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] sm:items-center"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-semibold text-foreground">
                        {c.label}
                      </span>
                      {c.is_set ? (
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                          Overridden
                        </span>
                      ) : (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                          Shared default
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                      {c.is_set ? c.masked_value : "— using platform value —"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type={c.secret ? "password" : "text"}
                      autoComplete="new-password"
                      placeholder={
                        c.is_set
                          ? "Enter new value to replace"
                          : "Set override…"
                      }
                      value={edits[c.key] ?? ""}
                      disabled={!databaseReady}
                      onChange={(e) =>
                        setEdits((prev) => ({
                          ...prev,
                          [c.key]: e.target.value,
                        }))
                      }
                    />
                    {(c.is_set || edited) && (
                      <Btn
                        variant="ghost"
                        size="iconSm"
                        title="Revert to shared default"
                        disabled={!databaseReady}
                        onClick={() =>
                          setEdits((prev) => ({ ...prev, [c.key]: "" }))
                        }
                      >
                        <Icon name="refresh" size={14} />
                      </Btn>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      ))}

      {databaseReady && (
        <div className="flex items-center justify-end gap-3">
          {dirty && (
            <Btn
              variant="outline"
              onClick={() => setEdits({})}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Btn>
          )}
          <Btn
            icon="check"
            onClick={save}
            disabled={!dirty || updateMutation.isPending}
          >
            {updateMutation.isPending ? "Saving…" : "Save credentials"}
          </Btn>
        </div>
      )}
    </div>
  );
}

/* ---------- Main Page ---------- */
export default function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: tenant, isLoading } = useTenant(id!);
  const updateMutation = useUpdateTenant();
  const deployMutation = useDeployTenant();
  const [tab, setTab] = useState("info");

  const isDeploying = tenant?.deployment_status === "deploying" || deployMutation.isPending;
  const isDeployed = tenant?.deployment_status === "deployed";
  useDeploymentPoller(id!, isDeploying);

  const { data: readiness } = useDeployReadiness(id!);
  const canDeploy = readiness?.ready && !isDeploying;

  const latestJob = tenant?.deployment_jobs?.[0] ?? null;

  const handleDeploy = async () => {
    if (!tenant || isDeploying) return;
    if (!readiness?.ready) {
      toast.error(
        readiness?.issues?.[0] || "Tenant is not ready for deployment",
      );
      return;
    }
    try {
      await deployMutation.mutateAsync(tenant.id);
      toast.success("Deployment started");
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string; issues?: string[] } } };
      const issues = axiosErr?.response?.data?.issues;
      const msg = issues?.length
        ? issues[0]
        : axiosErr?.response?.data?.detail || "Failed to start deployment";
      toast.error(msg);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-80" />
        <Skeleton className="h-28 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-20">
        <Icon name="alert" size={32} className="text-muted-foreground" />
        <p className="text-[14px] font-medium text-foreground">
          Tenant not found
        </p>
        <Btn variant="outline" onClick={() => navigate("/tenants")}>
          Back to tenants
        </Btn>
      </div>
    );
  }

  const handleSave = async (data: Partial<Tenant>) => {
    try {
      await updateMutation.mutateAsync({ id: tenant.id, data });
      toast.success("Tenant updated");
    } catch {
      toast.error("Failed to update tenant");
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/tenants")}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-muted text-foreground"
          >
            <Icon name="arrowLeft" size={18} />
          </button>
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-muted text-muted-foreground">
            <Icon name="building" size={22} />
          </span>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight text-foreground">
                {tenant.name}
              </h1>
              <StatusBadge status={tenant.status} />
              <DeployBadge
                status={isDeploying ? "deploying" : tenant.deployment_status}
              />
              <TrialBadge tenant={tenant} />
            </div>
            <div className="mt-0.5 flex items-center gap-2 text-[12px] text-muted-foreground">
              <span className="font-mono">{tenant.slug}</span>
              {tenant.domain && (
                <>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1">
                    {tenant.domain}
                    <Icon name="external" size={11} />
                  </span>
                </>
              )}
              <span>·</span>
              <OriginBadge tenant={tenant} />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Btn
            variant="outline"
            icon="copy"
            size="sm"
            onClick={() => {
              navigator.clipboard.writeText(
                tenant.api_url || `${tenant.slug}.teelogix.io`,
              );
              toast.success("API URL copied");
            }}
          >
            Copy API URL
          </Btn>
          <Btn
            icon="rocket"
            onClick={handleDeploy}
            disabled={!canDeploy && !isDeployed}
            title={
              !readiness?.ready && readiness?.issues?.length
                ? readiness.issues.join("\n")
                : undefined
            }
          >
            {isDeploying ? "Deploying…" : "Deploy"}
          </Btn>
        </div>
      </div>

      {/* Deploy readiness warnings */}
      {!isDeployed && readiness && !readiness.ready && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/30">
          <div className="flex items-start gap-2">
            <Icon
              name="alert"
              size={16}
              className="mt-0.5 shrink-0 text-amber-600"
            />
            <div>
              <p className="text-[13px] font-medium text-amber-800 dark:text-amber-200">
                Cannot deploy — missing required information:
              </p>
              <ul className="mt-1 list-disc pl-4 text-[12px] text-amber-700 dark:text-amber-300">
                {readiness.issues.map((issue, i) => (
                  <li key={i}>{issue}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Deployment panel */}
      <DeploymentPanel
        status={isDeploying ? "deploying" : tenant.deployment_status}
        job={latestJob}
        isDeploying={isDeploying}
      />

      {/* Tabs */}
      <div className="flex items-center gap-1 overflow-x-auto border-b border-border">
        {DETAIL_TABS.map((tb) => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            className={cn(
              "relative flex items-center gap-2 whitespace-nowrap px-3 py-2.5 text-[13px] font-semibold transition-colors",
              tab === tb.id
                ? "text-primary"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Icon name={tb.icon} size={15} />
            {tb.label}
            {tab === tb.id && (
              <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-primary" />
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "info" && <InfoTab tenant={tenant} onSave={handleSave} />}
      {tab === "services" && <TenantServicesTab tenantId={tenant.id} />}
      {tab === "credentials" && <CredentialsTab tenant={tenant} />}
      {tab === "contacts" && <ContactsTab tenant={tenant} />}
      {tab === "users" && <TenantUsersTab tenantId={tenant.id} />}
      {tab === "config" && <TenantConfigTab tenantId={tenant.id} />}
      {tab === "load-test" && <LoadTestTab tenant={tenant} />}
    </div>
  );
}
